import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { ApiError } from '../lib/api';
import { isRtl } from '../i18n/rtl';
import { getStory, type ImageField, type Scene, type Story } from '../lib/stories';
import './StoryDetail.css';

type Spread =
  | { kind: 'front'; image: ImageField | null; title: string }
  | { kind: 'back'; image: ImageField | null; synopsis: string }
  | { kind: 'pages'; scene: Scene };

type PageSide = 'left' | 'right';

type State =
  | { kind: 'loading' }
  | { kind: 'ready'; story: Story; spreads: Spread[] }
  | { kind: 'not_found' }
  | { kind: 'unauthenticated' }
  | { kind: 'error'; message: string };

const TURN_DURATION_MS = 750;

function bookAspectStyle(pageAspect: string | undefined): React.CSSProperties {
  // Book = two pages side-by-side, so book aspect = (2*w) / h.
  const [w, h] = (pageAspect ?? '3:4').split(':').map(Number);
  const pw = Number.isFinite(w) && w > 0 ? w : 3;
  const ph = Number.isFinite(h) && h > 0 ? h : 4;
  return {
    ['--page-aspect' as never]: `${pw} / ${ph}`,
    ['--book-aspect' as never]: `${2 * pw} / ${ph}`,
  };
}

export function StoryDetail() {
  const { t } = useTranslation();
  const params = useParams<{ storyId: string }>();
  const storyId = Number(params.storyId);
  const [state, setState] = useState<State>({ kind: 'loading' });

  useEffect(() => {
    document.body.classList.add('story-detail');
    return () => {
      document.body.classList.remove('story-detail');
    };
  }, []);

  useEffect(() => {
    if (!Number.isFinite(storyId) || storyId <= 0) {
      setState({ kind: 'not_found' });
      return;
    }
    let cancelled = false;
    getStory(storyId)
      .then((story) => {
        if (cancelled) return;
        const spreads: Spread[] = [
          { kind: 'front', image: story.front_cover, title: story.title },
          ...story.scenes.map<Spread>((scene) => ({ kind: 'pages', scene })),
          { kind: 'back', image: story.back_cover, synopsis: story.synopsis },
        ];
        setState({ kind: 'ready', story, spreads });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError) {
          if (err.status === 404) return setState({ kind: 'not_found' });
          if (err.status === 401 || err.status === 403)
            return setState({ kind: 'unauthenticated' });
        }
        const message = err instanceof Error ? err.message : t('common.unknownError');
        setState({ kind: 'error', message });
      });
    return () => {
      cancelled = true;
    };
  }, [storyId, t]);

  return <Body state={state} />;
}

function Body({ state }: { state: State }) {
  const { t } = useTranslation();
  switch (state.kind) {
    case 'loading':
      return <p className="detail-status">{t('common.loading')}</p>;
    case 'unauthenticated':
      return (
        <p className="detail-status">
          <Trans i18nKey="common.notSignedIn" components={[<a href="/" key="0" />]} />
        </p>
      );
    case 'not_found':
      return (
        <p className="detail-status">
          <Trans i18nKey="stories.notFound" components={[<Link to="/stories" key="0" />]} />
        </p>
      );
    case 'error':
      return (
        <p className="detail-status detail-error">
          {t('common.error', { message: state.message })}
        </p>
      );
    case 'ready':
      return <Book story={state.story} spreads={state.spreads} />;
  }
}

type Turn = { from: number; direction: 'next' | 'prev' };

function Book({ story, spreads }: { story: Story; spreads: Spread[] }) {
  const { t, i18n } = useTranslation();
  const [index, setIndex] = useState(0);
  const [turn, setTurn] = useState<Turn | null>(null);
  const bookRef = useRef<HTMLDivElement>(null);
  const rtl = isRtl(story.language);
  const uiRtl = isRtl(i18n.language);

  const clamp = useMemo(
    () => (i: number) => Math.max(0, Math.min(spreads.length - 1, i)),
    [spreads.length],
  );

  const goTo = useMemo(
    () => (target: number) => {
      setIndex((current) => {
        const clamped = clamp(target);
        if (clamped === current) return current;
        setTurn({ from: current, direction: clamped > current ? 'next' : 'prev' });
        return clamped;
      });
    },
    [clamp],
  );

  useEffect(() => {
    if (!turn) return;
    const t = window.setTimeout(() => setTurn(null), TURN_DURATION_MS);
    return () => window.clearTimeout(t);
  }, [turn]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // Under RTL, horizontal arrows are mirrored: → goes to the previous
      // spread, ← goes to the next. Vertical arrows + space are direction-
      // agnostic.
      const leftGoesPrev = !rtl;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goTo(leftGoesPrev ? index - 1 : index + 1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goTo(leftGoesPrev ? index + 1 : index - 1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        goTo(index - 1);
      } else if (e.key === 'ArrowDown' || e.key === ' ') {
        e.preventDefault();
        goTo(index + 1);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goTo, index, rtl]);

  useEffect(() => {
    const book = bookRef.current;
    if (!book) return;

    let touchX = 0;
    let touchY = 0;
    function onTouchStart(e: TouchEvent) {
      touchX = e.changedTouches[0].clientX;
      touchY = e.changedTouches[0].clientY;
    }
    function onTouchEnd(e: TouchEvent) {
      const dx = e.changedTouches[0].clientX - touchX;
      const dy = e.changedTouches[0].clientY - touchY;
      if (Math.abs(dx) < 40 || Math.abs(dy) > Math.abs(dx)) return;
      // LTR: swipe-left (dx<0) → next. RTL: swipe-right → next.
      const swipedForward = rtl ? dx > 0 : dx < 0;
      goTo(swipedForward ? index + 1 : index - 1);
    }
    function onClick(e: MouseEvent) {
      const target = e.target as HTMLElement | null;
      if (target?.closest('a, button')) return;
      const rect = book!.getBoundingClientRect();
      const x = e.clientX - rect.left;
      // LTR: left-third = prev, right-third = next. RTL: mirrored.
      const inLeftThird = x < rect.width / 3;
      const inRightThird = x > (rect.width * 2) / 3;
      if (inLeftThird) goTo(rtl ? index + 1 : index - 1);
      else if (inRightThird) goTo(rtl ? index - 1 : index + 1);
    }
    book.addEventListener('touchstart', onTouchStart, { passive: true });
    book.addEventListener('touchend', onTouchEnd);
    book.addEventListener('click', onClick);
    return () => {
      book.removeEventListener('touchstart', onTouchStart);
      book.removeEventListener('touchend', onTouchEnd);
      book.removeEventListener('click', onClick);
    };
  }, [goTo, index, rtl]);

  useEffect(() => {
    document.title = `${story.title} — Kinotale`;
  }, [story.title]);

  // Kick off a browser fetch for every image the book will ever show, as
  // soon as the story loads. Without this, each scene's <img> only mounts
  // when the reader flips to that spread — so the image pops in mid-turn.
  useEffect(() => {
    const urls: string[] = [];
    for (const spread of spreads) {
      if (spread.kind === 'pages') {
        if (spread.scene.composed_image?.url) urls.push(spread.scene.composed_image.url);
      } else if (spread.image?.url) {
        urls.push(spread.image.url);
      }
    }
    const preloaders = urls.map((src) => {
      const img = new Image();
      img.decoding = 'async';
      img.src = src;
      // Resolve the promise into nothing — we just want the decoded bitmap
      // warm in the browser's cache so the real <img> can paint in the same
      // frame it mounts.
      img.decode?.().catch(() => {});
      return img;
    });
    return () => {
      for (const img of preloaders) img.src = '';
    };
  }, [spreads]);

  return (
    <div className="book-wrapper" data-story-id={story.id} dir={rtl ? 'rtl' : 'ltr'}>
      {/* The nav is chrome, not story content, so it follows the UI
          language rather than the book's story language. */}
      <nav className="book-nav" dir={uiRtl ? 'rtl' : 'ltr'}>
        <Link to="/stories">{t('stories.backToStories')}</Link>
      </nav>
      <div
        className="book"
        ref={bookRef}
        style={bookAspectStyle(story.page_aspect)}
      >
        {/* Render the destination spread underneath throughout the turn so
            its <img> is already painted by the time the turn layers unmount
            — otherwise the .page-illustration black background flashes for
            one frame while the newly-mounted <img> decodes. */}
        <SpreadView spread={spreads[index]} />
        {turn && (
          <PageTurnLayers
            from={spreads[turn.from]}
            to={spreads[index]}
            direction={turn.direction}
            rtl={rtl}
          />
        )}
      </div>
      <div className="book-controls">
        <button
          type="button"
          className="book-btn book-btn-prev"
          aria-label={t('stories.previousPage')}
          disabled={index === 0}
          onClick={() => goTo(index - 1)}
        >
          ‹
        </button>
        <span className="book-page-indicator">
          {index + 1} / {spreads.length}
        </span>
        <button
          type="button"
          className="book-btn book-btn-next"
          aria-label={t('stories.nextPage')}
          disabled={index === spreads.length - 1}
          onClick={() => goTo(index + 1)}
        >
          ›
        </button>
      </div>
      <RotateHint />
    </div>
  );
}

function RotateHint() {
  const { t } = useTranslation();
  return (
    <div className="rotate-hint" role="status" aria-live="polite">
      <svg
        className="rotate-hint__icon"
        viewBox="0 0 24 24"
        width="40"
        height="40"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.6}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <rect x="4" y="2" width="11" height="20" rx="2" />
        <path d="M10 18h0" />
        <path d="M17 7l3 3-3 3" />
        <path d="M20 10h-6" />
      </svg>
      <div className="rotate-hint__text">{t('stories.rotateHint')}</div>
    </div>
  );
}

function PageTurnLayers({
  from,
  to,
  direction,
  rtl,
}: {
  from: Spread;
  to: Spread;
  direction: 'next' | 'prev';
  rtl: boolean;
}) {
  // "Forward" in the reading flow is `direction === 'next'`. In an LTR book
  // that flips the right page leftward; in an RTL book it flips the left page
  // rightward — a visual mirror. We express that by mapping `direction` to a
  // physical motion (`flip`): LTR-next / RTL-prev → flip-left (hinge on left,
  // trailing edge on right). LTR-prev / RTL-next → flip-right.
  const flip: 'left' | 'right' =
    (direction === 'next') === !rtl ? 'left' : 'right';

  // Which spread's outer half stays static on each side. For flip-left the
  // origin keeps its left half and destination keeps its right half; flip-
  // right is the inverse.
  const leftSpread = flip === 'left' ? from : to;
  const rightSpread = flip === 'left' ? to : from;

  // Each layer lives in a physical slot (left or right half of the book).
  // For scene pages, side='left'=text and side='right'=illustration are
  // *logical*: the stable spread is flex-row inside a dir=rtl wrapper so its
  // children visually reverse, but turn layers are absolutely positioned and
  // don't respect `dir`, so physical-left must request the logical side that
  // renders illustration (i.e. 'right' under RTL). For covers, side='left'/
  // 'right' is a *physical* trick — the width:200% shift in PageFace picks
  // which physical half of the cover image shows through the clipped window.
  // That means covers must receive their physical slot directly; applying
  // the scene-logic swap would render the cover's halves swapped at 0° and
  // desync the first frame of the turn from the pre-turn stable view.
  const sideFor = (spread: Spread, slot: PageSide): PageSide => {
    if (spread.kind !== 'pages') return slot;
    return rtl ? (slot === 'left' ? 'right' : 'left') : slot;
  };
  // Flap physical position: flip='left' sits on physical right at 0° and
  // lands on physical left at 180°; flip='right' is the inverse.
  const flapFrontSlot: PageSide = flip === 'left' ? 'right' : 'left';
  const flapBackSlot: PageSide = flip === 'left' ? 'left' : 'right';

  return (
    <>
      <div className="book-underlay book-underlay--left">
        <PageFace spread={leftSpread} side={sideFor(leftSpread, 'left')} />
      </div>
      <div className="book-underlay book-underlay--right">
        <PageFace spread={rightSpread} side={sideFor(rightSpread, 'right')} />
      </div>
      <div className={`page-flap page-flap--${flip}`}>
        <div className="page-flap__face page-flap__face--front">
          <PageFace spread={from} side={sideFor(from, flapFrontSlot)} />
        </div>
        <div className="page-flap__face page-flap__face--back">
          <PageFace spread={to} side={sideFor(to, flapBackSlot)} />
        </div>
      </div>
    </>
  );
}

function SpreadView({ spread }: { spread: Spread }) {
  if (spread.kind === 'pages') {
    const className = 'book-spread spread-pages';
    return (
      <div className={className} data-active="true">
        <PageFace spread={spread} side="left" />
        <PageFace spread={spread} side="right" />
      </div>
    );
  }
  return (
    <div className="book-spread spread-cover" data-active="true">
      <PageFace spread={spread} side="full" />
    </div>
  );
}

function PageFace({ spread, side }: { spread: Spread; side: PageSide | 'full' }) {
  const { t } = useTranslation();
  if (spread.kind === 'pages') {
    const { scene } = spread;
    const paletteStyle =
      scene.palette && scene.palette.length >= 3
        ? ({
            '--c1': scene.palette[0],
            '--c2': scene.palette[1],
            '--c3': scene.palette[2],
          } as React.CSSProperties)
        : undefined;

    if (side === 'left') {
      return (
        <div className="page page-text" style={paletteStyle}>
          {scene.book_text && (
            <div className="page-text-content">
              {scene.book_text.split('\n').map((line, idx, arr) => (
                <span key={idx}>
                  {line}
                  {idx < arr.length - 1 && <br />}
                </span>
              ))}
            </div>
          )}
          <div className="page-number">{scene.number}</div>
        </div>
      );
    }

    const hasImage = Boolean(scene.composed_image?.url);
    // Render the scene image as a CSS background rather than an <img> tag.
    // A freshly-mounted <img> has its own async decode step that flashes the
    // page's black background for one frame even when bytes are preloaded;
    // a cached background-image paints synchronously on mount.
    return (
      <div
        className="page page-illustration"
        role="img"
        aria-label={t('stories.sceneAlt', { number: scene.number })}
        style={
          hasImage
            ? { backgroundImage: `url(${scene.composed_image!.url})` }
            : undefined
        }
      >
        {!hasImage && <div className="illustration-placeholder" />}
      </div>
    );
  }

  // Cover spreads span the full book. When rendered as a half (during a
  // page turn), render the .page at full book width shifted horizontally so
  // the matching half shows through the enclosing overflow-hidden half —
  // this keeps the image AND text overlay identical to the stable cover
  // view, just clipped.
  //
  // Position the half-rendered cover absolutely rather than relatively. A
  // relative block box wider than its parent anchors on the inline-start
  // edge, which flips with `dir`: under RTL the .page would snap its right
  // edge to the parent's right, sliding the image-half window 50% of book
  // width off and surfacing the wrong half. `left` on an absolute element
  // is physical, so the same 0 / -100% values hold in both directions.
  const isFront = spread.kind === 'front';
  const coverAlt = isFront ? t('stories.frontCover') : t('stories.backCover');
  const baseHalfStyle: React.CSSProperties | undefined =
    side === 'left'
      ? { position: 'absolute', top: 0, width: '200%', height: '100%', left: 0 }
      : side === 'right'
        ? { position: 'absolute', top: 0, width: '200%', height: '100%', left: '-100%' }
        : undefined;
  const coverStyle: React.CSSProperties | undefined = spread.image?.url
    ? { ...baseHalfStyle, backgroundImage: `url(${spread.image.url})` }
    : baseHalfStyle;

  return (
    <div
      className={`page page-cover ${isFront ? 'page-front-cover' : 'page-back-cover'}`}
      role="img"
      aria-label={coverAlt}
      style={coverStyle}
    >
      {!spread.image?.url && <div className="cover-placeholder" />}
      {isFront ? (
        <div className="cover-text cover-text-front">
          <h1 className="cover-title">{spread.title}</h1>
          <p className="cover-subtitle">{t('stories.coverSubtitle')}</p>
        </div>
      ) : (
        <div className="cover-text cover-text-back">
          <p className="back-synopsis">{spread.synopsis}</p>
        </div>
      )}
    </div>
  );
}
